import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { clearPrescriptionFileName, getLabelFallback, roundNumber } from '@utils/utils';
import { useEffect, useRef, useState } from 'react';

import Bitmap from '@images/svg-icons/Bitmap.svg';

type HowToSubmitProps = {
    isEditingPrescription: boolean;
    prescriptionId?: string;
    uploadPrescriptionError: boolean;
    uploadFile: File;
    uploadFileImage: string | ArrayBuffer;
    setUploadFile: (file: File) => void;
    setUploadFileImage: (fileImage: string | ArrayBuffer) => void;
    isUploadClicked: boolean;
    setIsUploadClicked: (isUploadClicked: boolean) => void;
};

const HowToSubmit = (props: HowToSubmitProps) => {
    const [isPrescriptionFileSelected, setIsPrescriptionFileSelected] = useState(false);
    const [errorImg, setErrorImg] = useState(false);
    const [errorFileBig, setErrorFileBig] = useState(false);
    const fileInputRef = useRef(null);

    const handleButtonClick = () => {
        fileInputRef.current.click();
    }

    useEffect(() => {
        /**
         * If editing prescription and uploaded file has been attached, show the file details in the form
         */
        if (props?.isEditingPrescription && props?.isUploadClicked && !!props?.uploadFile) {
            insertFile(props?.uploadFile);
        }
    }, [props?.isUploadClicked, props?.uploadFile]);

    useEffect(() => {
        if (isPrescriptionFileSelected) {
            const differentFileElem: HTMLElement = document.getElementById('different-file');

            if (!!differentFileElem) {
                differentFileElem.addEventListener('change', function (e: Event) {
                    const differentFileInputElem: HTMLInputElement = e.target as HTMLInputElement;

                    setErrorFileBig(false);

                    if (differentFileInputElem.files[0].size / 1024 / 1024 > 11) {
                        setErrorFileBig(true);
                    } else {
                        if (differentFileInputElem.files[0]) {
                            insertFile(differentFileInputElem.files[0]);
                        }
                    }
                });
            }
        } else {
            const prescriptionFileElem: HTMLElement = document.getElementById('prescription-file');

            if (prescriptionFileElem) {
                prescriptionFileElem.addEventListener('change', function (e: Event) {
                    const prescriptionFileInputElem: HTMLInputElement =
                        e.target as HTMLInputElement;

                    setErrorFileBig(false);

                    if (prescriptionFileInputElem.files[0].size / 1024 / 1024 > 11) {
                        setErrorFileBig(true);
                    } else {
                        if (prescriptionFileInputElem.files[0]) {
                            insertFile(prescriptionFileInputElem.files[0]);
                        }
                    }
                });
            }
        }
    }, [isPrescriptionFileSelected]);

    const insertFile = (file: File) => {
        var reader = new FileReader();

        reader.onload = async function (e) {
            props.setUploadFile(file);
            props.setUploadFileImage(e.target.result);
            setErrorImg(false);
            setIsPrescriptionFileSelected(true);
        };

        reader.readAsDataURL(file);
    };

    const removePrescriptionFile = () => {
        setIsPrescriptionFileSelected(false);

        props.setUploadFile(null);

        const prescriptionFileElem: HTMLElement = document.getElementById('prescription-file');

        if (!!prescriptionFileElem) {
            const prescriptionFileInputElem: HTMLInputElement =
                prescriptionFileElem as HTMLInputElement;
            prescriptionFileInputElem.value = null;
        }

        const differentFileElem: HTMLElement = document.getElementById('different-file');

        if (!!differentFileElem) {
            const differentFileInputElem: HTMLInputElement = differentFileElem as HTMLInputElement;
            differentFileInputElem.value = null;
        }
    };

    return (
        <div className="how-to-submit">
            <div className="section-title">CHOOSE HOW TO SEND YOUR PRESCRIPTION</div>

            {/* TODO - Check if data analytics is correct */}
            <RadioGroup
                value={!!props?.isUploadClicked ? 'upload' : 'callDoctor'}
                onChange={e => props.setIsUploadClicked(e.target.value === 'upload')}
            >
                <FormControlLabel
                    value="upload"
                    className="mb-8"
                    control={
                        <Radio
                            sx={{
                                color: 'black',
                                '&.Mui-checked': {
                                    color: 'black',
                                },
                            }}
                            data-analytics_available_call="0"
                        />
                    }
                    label="Upload it"
                />

                {!isPrescriptionFileSelected && (
                    <>
                        <div className='how-to-submit-text'>
                            <span className='how-to-submit-text-title'>{getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_UPLOAD_IT_TEXT_1')}</span>
                            <span className='how-to-submit-text-subtitle'>{getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_UPLOAD_IT_TEXT_2')}</span>
                        </div>
                        <div className="browse mb-32">
                            <input id="prescription-file" ref={fileInputRef} type="file" style={{ display: 'none' }} data-element-id="MyPrescription_UploadFile" />

                            <button
                                className="primary-button-negative small browse-button"
                                data-element-id="MyPrescription_UploadFile"
                                onClick={handleButtonClick}
                                type='button'
                            >
                                UPLOAD FILE
                            </button>
                        </div>
                    </>
                )}

                {isPrescriptionFileSelected && (
                    <div className="prescription-uploaded-file-preview">
                        <div className="image-preview-container">
                            {!errorImg ? (
                                <img
                                    src={props.uploadFileImage.toString()}
                                    onError={() => setErrorImg(true)}
                                ></img>
                            ) : (
                                <Bitmap />
                            )}
                        </div>
                        <div className="name-actions-container">
                            <div className="file-name-container">
                                {clearPrescriptionFileName(
                                    props.uploadFile?.name,
                                    props?.prescriptionId
                                )}{' '}
                                – [{roundNumber(props.uploadFile?.size / 1024 / 1024) + 'MB'}]
                            </div>
                            <div className="upload-different-file-container">
                                <input
                                    id="different-file"
                                    type="file"
                                    style={{ display: 'none' }}
                                />
                                <label
                                    htmlFor="different-file"
                                    className="link link-underline"
                                    data-analytics_available_call="0"
                                >
                                    Upload different file
                                </label>
                            </div>
                            <div className="remove-container">
                                <a className="remove-link" onClick={removePrescriptionFile}>
                                    <span className="link link-underline">Remove</span>
                                </a>
                            </div>
                        </div>
                    </div>
                )}

                {props.uploadPrescriptionError && props.isUploadClicked && (
                    <div className="upload-prescription-error">
                        You need to upload your prescription.
                    </div>
                )}
                {!props.uploadPrescriptionError && props.isUploadClicked && errorFileBig && (
                    <div className="upload-prescription-error">
                        {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_FILE_TOO_BIG_LABEL')}
                    </div>
                )}

                <FormControlLabel
                    value="callDoctor"
                    control={
                        <Radio
                            sx={{
                                color: 'black',
                                '&.Mui-checked': {
                                    color: 'black',
                                },
                            }}
                            data-analytics_available_call="0"
                        />
                    }
                    label="Have us call your doctor"
                />
            </RadioGroup>

            <div className="note-processing">
                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_UPLOAD_IT_NOTE_PROCESSING')}
            </div>
        </div>
    );
};

export default HowToSubmit;
